import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api, { formatIDR, formatDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import SearchBar from "@/components/SearchBar";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, ArrowRight, Calculator } from "@phosphor-icons/react";

export default function Sales() {
  const [sales, setSales] = useState([]);
  const [q, setQ] = useState("");
  const [petugasFilter, setPetugasFilter] = useState("all");

  useEffect(() => {
    api.get("/sales").then((r) => setSales(r.data));
  }, []);

  const petugasList = useMemo(() => {
    const set = new Set();
    sales.forEach((s) => { if (s.makelar_name) set.add(s.makelar_name); });
    return Array.from(set).sort();
  }, [sales]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    let list = sales;
    if (petugasFilter && petugasFilter !== "all") {
      list = list.filter((sl) => (sl.makelar_name || "") === (petugasFilter === "__none__" ? "" : petugasFilter));
    }
    if (!s) return list;
    return list.filter((sl) =>
      (sl.account_number || "").toLowerCase().includes(s) ||
      (sl.customer?.name || "").toLowerCase().includes(s) ||
      (sl.customer?.phone || "").toLowerCase().includes(s) ||
      (sl.car?.brand || "").toLowerCase().includes(s) ||
      (sl.car?.model || "").toLowerCase().includes(s) ||
      (sl.car?.plate_number || "").toLowerCase().includes(s) ||
      (sl.makelar_name || "").toLowerCase().includes(s)
    );
  }, [sales, q, petugasFilter]);

  return (
    <div>
      <PageHeader
        title="Penjualan"
        subtitle="Daftar transaksi cash dan kredit"
        actions={
          <div className="flex gap-2">
            <Link to="/calculator">
              <Button data-testid="header-calc" className="rounded-none bg-white border border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white uppercase tracking-wider text-xs h-10">
                <Calculator size={14} className="mr-2" /> Kalkulator
              </Button>
            </Link>
            <Link to="/sales/new">
              <Button data-testid="new-sale-button" className="rounded-none bg-[#0A0A0A] hover:bg-gray-800 text-white uppercase tracking-wider text-xs h-10">
                <Plus size={14} className="mr-2" />
                Transaksi Baru
              </Button>
            </Link>
          </div>
        }
      />

      <div className="p-4 md:p-6 lg:p-10">
        <div className="flex items-start gap-3 mb-4">
          <div className="flex-1 max-w-md">
            <SearchBar value={q} onChange={setQ} placeholder="Cari no. rekening / pelanggan / mobil / plat..." testid="sales-search" count={filtered.length} />
          </div>
          <div className="min-w-[220px]">
            <Select value={petugasFilter} onValueChange={setPetugasFilter}>
              <SelectTrigger className="rounded-none" data-testid="petugas-filter"><SelectValue placeholder="Semua petugas" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Petugas</SelectItem>
                <SelectItem value="__none__">— Belum Ditetapkan —</SelectItem>
                {petugasList.map((p) => (<SelectItem key={p} value={p}>{p}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="border border-gray-200 overflow-x-auto overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-left text-[10px] uppercase tracking-widest text-gray-600">
                <th className="p-4">No. Rekening</th>
                <th className="p-4">Tanggal</th>
                <th className="p-4">Pelanggan</th>
                <th className="p-4">Mobil</th>
                <th className="p-4">Makelar</th>
                <th className="p-4">Tipe</th>
                <th className="p-4 text-right">Harga</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`sale-row-${s.id}`}>
                  <td className="p-4 font-mono-num font-semibold text-xs">{s.account_number || "-"}</td>
                  <td className="p-4 font-mono-num text-xs">{formatDate(s.sale_date)}</td>
                  <td className="p-4">{s.customer?.name || "-"}</td>
                  <td className="p-4 text-gray-600">{s.car ? `${s.car.brand} ${s.car.model}${s.car.type ? " · " + s.car.type : ""}` : "-"}</td>
                  <td className="p-4 text-xs text-gray-700">{s.makelar_name || "-"}</td>
                  <td className="p-4">
                    <span className={`inline-block px-2 py-1 text-[10px] uppercase tracking-wider ${s.sale_type === "cash" ? "bg-gray-900 text-white" : "border border-gray-900 text-gray-900"}`}>
                      {s.sale_type}
                    </span>
                  </td>
                  <td className="p-4 text-right font-mono-num font-semibold">{formatIDR(s.total_price)}</td>
                  <td className="p-4 text-right">
                    <Link to={`/sales/${s.id}`} className="inline-flex items-center gap-1 text-xs uppercase tracking-wider hover:underline" data-testid={`sale-view-${s.id}`}>
                      Detail <ArrowRight size={12} />
                    </Link>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan="8" className="p-10 text-center text-sm text-gray-500">{sales.length === 0 ? "Belum ada transaksi" : "Tidak ada hasil"}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
